import { useEffect, useMemo, useState } from 'react'
import { slugToQuery } from '../utils'
import { useBoolean, useLocation } from 'react-use'
import { assign, filter, get, includes, isFunction, map, omit, size } from 'lodash-es'
import { HOUSE_TYPE_ID } from '@config'
import { combinePoints, getHouseSearchTypeData, joinStr, string2query } from '@utils'
import useFetch from '@hooks/useFetch'
import { buildUrl } from '@plugin/http/lib/utils'
import { houseSearchType } from '@routerConfig'
import { statsHouseList } from '@plugin/statistics'

export default function useMapData({
  state,
  fullMap,
  resetMarkerList,
  bounds,
  rect
}) {

  const { type } = state
  const { typeId } = getHouseSearchTypeData({ type })
  // 路由上的参数
  const routeParams = useMemo(() => slugToQuery(state).toString(), [state])
  // show for sale

  const [showSale, toggleSale] = useBoolean(
    [HOUSE_TYPE_ID.residential, HOUSE_TYPE_ID['commercial-property-for-sale'], HOUSE_TYPE_ID.village].includes(typeId)
  )
  // show for rent
  const [showRent, toggleRent] = useBoolean(typeId === HOUSE_TYPE_ID.rent)
  // show sold
  const [showSold, toggleSold] = useBoolean(typeId === HOUSE_TYPE_ID.sold)
  // show estimate
  const [showEstimate, toggleEstimate] = useBoolean(false)

  // 当前地图sale的数据
  const [salePoints, saleLoading] = useTypeData({ rect, bounds, state, routeParams, typeId, visible: showSale }, resetMarkerList)
  // 当前地图rent的数据
  const [rentPoints, rentLoading] = useTypeData({ rect, bounds, state, routeParams, typeId: HOUSE_TYPE_ID.rent, visible: showRent }, resetMarkerList)
  // 当前地图sold的数据
  const [soldPoints, soldLoading] = useTypeData({ rect, bounds, state, routeParams, typeId: HOUSE_TYPE_ID.sold, visible: showSold }, resetMarkerList)
  // 当前地图estimate数据
  const [estimatePoints, estimateLoading] = usePropertyData({ rect, bounds, state, routeParams, showEstimate })

  // 左边房源列表搜索
  const [listData, listLoading, listParams] = useTypeData({ rect, bounds, state, routeParams, visible: !fullMap, getPoint: false })
  const listTotals = useMemo(() => get(listData, 'headers.x-total-count', 0), [listData])

  return {
    salePoints: combinePoints(salePoints),
    rentPoints: combinePoints(rentPoints),
    soldPoints: combinePoints(soldPoints),
    estimatePoints: combinePoints(estimatePoints),
    isLoading: saleLoading || rentLoading || soldLoading || estimateLoading,
    listData,
    listParams,
    listLoading,
    listTotals,
    showSale,
    showRent,
    showSold,
    showEstimate,
    toggleSale,
    toggleRent,
    toggleSold,
    toggleEstimate
  }
}

function useTypeData({
  rect,
  bounds,
  state,
  routeParams,
  typeId,
  visible,
  getPoint = 1
}, resetMarkerList) {

  const local = useLocation()
  const [mapData, setMapData] = useState([])
  const params = useMemo(() => {
    if (!rect) return
    const rIds = map(filter(mapData, item => item.lng && item.lat ? bounds.contains([item.lng, item.lat]) : false), item => item.id)
    const { offset, limit } = routeParams
    const mapLimit = limit * 4
    const mapOffset = Math.floor(offset / limit / 4) * mapLimit

    return assign(
      { rect, showcasePriority: 1 },
      includes(local.search, 'center=') ? omit(routeParams, ['suburbId', 'districtId', 'regionId']) : routeParams,
      size(rIds) ? { rIds } : undefined,
      typeId ? { typeId } : undefined,
      getPoint ? { getPoint, offset: mapOffset, limit: mapLimit } : undefined,
      get(state, 'type') === houseSearchType.newHomes ? { isProject: 1 } : undefined,
    )
  }, [rect, bounds, state, routeParams, mapData, typeId, getPoint, local])
  const { data, isLoading } = useFetch(
    visible && params
      ? { url: 'houses/list', params, fetchKey: buildUrl('houses/list', undefined, omit(params, 'rIds')) }
      : null,
    {
      keepPreviousData: true,
      onSuccess: (data, key) => {
        // 地图数据重新加载，清空缓存的marker
        if (routeParams.typeId === typeId && isFunction(resetMarkerList)) resetMarkerList()
        // 房源搜索统计
        const params = string2query(key)
        statsHouseList(joinStr(map(data, 'id'), ','), params)
      }
    }
  )
  useEffect(() => setMapData(data), [data, setMapData])

  return [
    visible ? data : [],
    isLoading,
    params
  ]
}

function usePropertyData({
  rect,
  bounds,
  routeParams,
  showEstimate
}) {

  const local = useLocation()
  const [mapData, setMapData] = useState([])
  const visible = showEstimate
  const params = useMemo(() => {
    if (!rect) return
    const rIds = map(filter(mapData, item => bounds.contains([item.lng, item.lat])), item => item.id)
    const mapTypeIds = joinStr([showEstimate ? HOUSE_TYPE_ID.estimate : void 0])
    const routeParam = omit(routeParams, 'typeId')
    return assign(
      { rect },
      includes(local.search, 'center=') ? omit(routeParam, ['suburbId', 'districtId', 'regionId']) : routeParam,
      size(rIds) ? { rIds } : undefined,
      mapTypeIds ? { mapTypeIds } : undefined
    )
  }, [rect, bounds, routeParams, mapData, showEstimate, local])
  const { data, isLoading } = useFetch(
    visible && params
      ? { url: 'property-map', params, fetchKey: buildUrl('property-map', undefined, omit(params, 'rIds')) }
      : null,
    {
      keepPreviousData: true,
      onSuccess: (data, key) => {
        // 房源搜索统计
        const params = string2query(key)
        statsHouseList(joinStr(map(data, 'id'), ','), params)
      }
    }
  )
  useEffect(() => setMapData(data), [data, setMapData])

  return [
    visible ? data : [],
    isLoading
  ]
}